// Gegnerwahl im Spielmodus: Stockfish, Maia (menschlich) oder Rodent mit Persönlichkeit, je mit Stärkeregler.
import { h, type VNode } from 'snabbdom';
import { t } from '../i18n';
import { MAIA, PLAYERS, RODENT, STYLES, personalityName, type BotKind } from '../playbots';
import type { PlayCtrl } from '../playctrl';
import { ELO_BEGIN, ELO_FULL, eloLabel } from '../playelo';

const KINDS: [BotKind, string][] = [['stockfish', 'Stockfish'], ['maia', 'Mensch'], ['rodent', 'Großmeister']];

/** Erklärung unter dem Regler, im Rahmen der Felder */
const hint = (text: string) => h('div.fld', h('p.muted.small', text));

function slider(label: string, out: string, min: number, max: number, step: number, value: number, set: (v: number) => void): VNode {
  return h('div.fld', [
    h('div.row', [h('span', label), h('output', out)]),
    h('input', {
      attrs: { type: 'range', 'aria-label': t('Spielstärke') },
      props: { min, max, step, value },
      on: { input: (e: Event) => set(+(e.target as HTMLInputElement).value) },
    }),
  ]);
}

function personalities(c: PlayCtrl): VNode {
  const group = (label: string, ids: string[]) =>
    h('optgroup', { attrs: { label } }, ids.map(id => h('option', { attrs: { value: id }, props: { selected: c.bot.personality === id } }, personalityName(id))));
  return h('div.row', [
    h('label', { attrs: { for: 'bot-personality' } }, t('Vorbild')),
    h('select#bot-personality', { on: { change: (e: Event) => c.setBot({ personality: (e.target as HTMLSelectElement).value }) } }, [
      group(t('Berühmte Spieler'), Object.keys(PLAYERS)),
      group(t('Stile'), Object.keys(STYLES)),
    ]),
  ]);
}

/** Während einer laufenden Partie gesperrt, damit Gegner und gespeicherte Partie zusammenpassen */
export function botPick(c: PlayCtrl): VNode {
  return h('fieldset.bot-pick', { attrs: { disabled: c.started && !c.result } }, controls(c));
}

function controls(c: PlayCtrl): VNode[] {
  const b = c.bot;
  const kinds = h('div.fld', h('div.seg', { attrs: { role: 'group', 'aria-label': t('Gegner') } }, KINDS.map(([k, label]) =>
    h('button.btn.seg-btn', {
      class: { active: b.kind === k },
      attrs: { disabled: !c.available[k], 'aria-pressed': String(b.kind === k) },
      on: { click: () => c.setBot({ kind: k }) },
    }, t(label)))));
  if (b.kind === 'maia') {
    return [kinds,
      slider('Maia', String(b.maiaLevel), MAIA.min, MAIA.max, MAIA.step, b.maiaLevel, v => c.setBot({ maiaLevel: v })),
      hint(t('Zieht wie Menschen dieser Stärke, mit ihren typischen Fehlern (Maia, Universität Toronto). Ein Zug ohne Vorausrechnen.'))];
  }
  if (b.kind === 'rodent') {
    return [kinds,
      h('div.fld', [personalities(c)]),
      slider(personalityName(b.personality), `${b.rodentElo} Elo`, RODENT.min, RODENT.max, RODENT.step, b.rodentElo, v => c.setBot({ rodentElo: v })),
      hint(t('Der Stil ist über Bewertung und Eröffnungsbuch nachempfunden (Rodent IV), keine Kopie des Spielers.'))];
  }
  return [kinds,
    slider('Stockfish', eloLabel(b.stockfishElo), ELO_BEGIN, ELO_FULL, 50, b.stockfishElo, v => c.setBot({ stockfishElo: v })),
    hint(t('Ab 1350 Elo geeicht über Stockfishs Elo-Begrenzung (UCI_Elo), Richtwerte. Darunter vier Einsteiger-Stufen ohne gemessene Elo; echte Elo-Werte ab 800 bietet „Großmeister“.'))];
}
